import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Input, message, Modal, Radio, Row, Select } from 'antd';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';

import DateTimePicker from 'components/DateTimePicker';
import ModalFormFooter from 'components/Form/ModalFormFooter';
import { FakeUploadButton, FormItem } from 'components/Layout/Form';
import Label from 'components/Text/Label';
import TimezonePicker from 'components/TimezonePicker';
import { makeSelectTenantTimezone } from 'containers/App/selectors';
import { convertUrlToFile, formItemLayout } from 'containers/BDO/utils';

import { createBDOSetting, getBDOSetting, updateBDOSetting } from '../action';
import { creditCardsGet } from '../creditCard/actions';
import {
  IBDOSetting,
  IBDOSettingsState,
  ICreditCard,
  AddEditVouchersModalProps,
  CreateBDOSettingFormValues,
  CreateBDOSettingPayload,
  UpdateBDOSettingPayload
} from '../types';

interface FormValues extends CreateBDOSettingFormValues {}

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  item_type: yup.string().required('Type is required'),
  cost: yup
    .number()
    .typeError('Cost must be a number')
    .integer('Cost must be an integer')
    .min(1, 'Cost must be greater than 0')
    .required('Cost is required'),
  category: yup.string().required('Category is required'),
  lrs_reward_code: yup
    .string()
    .matches(/^[a-zA-Z0-9]+$/, 'LRS Reward Code must contain only letters and numbers')
    .required('LRS Reward Code is required'),
  card_ids: yup
    .array()
    .min(1, 'Applicable Credit Cards are required')
    .required('Applicable Credit Cards are required'),
  start_date: yup
    .date()
    .nullable()
    .required('Start Date is required')
    .typeError('Invalid Start Date'),
  end_date: yup
    .date()
    .nullable()
    .required('End Date is required')
    .typeError('Invalid End Date')
    .min(yup.ref('start_date'), 'End Date must be after Start Date')
});

const AddEditVouchersModal: React.FC<AddEditVouchersModalProps> = ({
  selectedVouchers = null,
  modalOpen,
  closeModal,
  isEdit = false
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { bdoSetting }: { bdoSetting: IBDOSetting | null; loading: boolean; error: any } =
    useSelector((state: { bdoSettings: IBDOSettingsState }) => state.bdoSettings);
  const { creditCards } = useSelector((state: any) => state.creditCards);
  const tenantTimezone: string = useSelector(makeSelectTenantTimezone()) || 'UTC';

  useEffect(() => {
    if (modalOpen) {
      dispatch(creditCardsGet({ size: 200 }));
    }
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (modalOpen && selectedVouchers && isEdit) {
      dispatch(getBDOSetting(selectedVouchers));
    }
  }, [modalOpen, selectedVouchers, isEdit, dispatch]);

  const initialValues = {
    name:
      isEdit && bdoSetting
        ? bdoSetting.name
        : !isEdit && selectedVouchers
        ? bdoSetting
          ? `Copy of ${bdoSetting.name}`
          : ''
        : '',
    item_type: bdoSetting?.item_type || 'evoucher',
    cost: bdoSetting?.cost || '',
    card_ids: bdoSetting?.cards?.map((card: ICreditCard) => card.id) || [],
    category: bdoSetting?.category || '',
    lrs_reward_code: bdoSetting?.lrs_reward_code || '',
    start_date: bdoSetting?.start_date ? new Date(bdoSetting.start_date) : null,
    end_date: bdoSetting?.end_date ? new Date(bdoSetting.end_date) : null,
    image: bdoSetting?.image
      ? typeof bdoSetting?.image === 'string'
        ? { url: bdoSetting?.image }
        : bdoSetting?.image
      : null
  };

  const cardOptions =
    creditCards?.data?.map((card: ICreditCard) => ({
      label: card.product_code,
      value: card.id
    })) || [];

  const onSave = async (
    values: FormValues,
    {
      setSubmitting,
      resetForm
    }: { setSubmitting: (isSubmitting: boolean) => void; resetForm: () => void }
  ) => {
    const onSuccess = () => {
      setSubmitting(false);
      resetForm();
      closeModal();
      if (!isEdit) {
        navigate(`/p/credit_card_redemption_settings/vouchers/list`);
      }
    };

    const onError = () => {
      setSubmitting(false);
    };
    try {
      const apiValues: CreateBDOSettingPayload | UpdateBDOSettingPayload = {
        ...values,
        cost: parseFloat(values.cost as string),
        start_date: values.start_date
          ? typeof values.start_date === 'string'
            ? values.start_date
            : values.start_date.toISOString()
          : '',
        end_date: values.end_date
          ? typeof values.end_date === 'string'
            ? values.end_date
            : values.end_date.toISOString()
          : '',
        item_type: values.item_type as any
      };

      if (isEdit && selectedVouchers) {
        dispatch(
          updateBDOSetting(
            { ...(apiValues as UpdateBDOSettingPayload), id: selectedVouchers },
            onSuccess,
            onError
          )
        );
      } else {
        if (!values.image?.file) {
          if (values.image?.url) {
            try {
              const fileFromUrl = await convertUrlToFile(values.image.url);
              apiValues.images = [{ file: fileFromUrl, filename: values.name }];
            } catch (error) {
              message.error('The image from the provided link could not be processed.');
              setSubmitting(false);
              return;
            }
          } else {
            message.error('An image file is required.');
            setSubmitting(false);
            return;
          }
        }
        dispatch(createBDOSetting(apiValues as CreateBDOSettingPayload, onSuccess, onError));
        navigate(`/p/credit_card_redemption_settings/vouchers/list`);
      }

      resetForm();
      closeModal();
    } catch (error) {
      message.error('Failed to save voucher.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      destroyOnClose
      open={modalOpen}
      title="Enter Voucher Details"
      footer={null}
      onCancel={closeModal}
      width={1000}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSave}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, resetForm, setFieldValue }) => {
          return (
            <Form>
              <Field name={'name'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Name'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input allowClear placeholder={'Voucher Name'} {...field} />
                    </FormItem>
                  );
                }}
              </Field>
              <FormItem {...formItemLayout} label="Type" required>
                <Field name="item_type">
                  {({ field }) => (
                    <Radio.Group {...field}>
                      <Radio value="evoucher">Evoucher</Radio>
                      <Radio value="paper">Paper</Radio>
                    </Radio.Group>
                  )}
                </Field>
              </FormItem>
              <Field name="image">
                {({ field, meta }) => {
                  return (
                    <>
                      <FormItem label="Logo" required {...formItemLayout}>
                        <div style={{ display: 'block', textAlign: 'left' }}>
                          <label htmlFor="image_uploads">
                            <FakeUploadButton>Upload Image</FakeUploadButton>
                          </label>
                        </div>
                        <input
                          id="image_uploads"
                          style={{
                            opacity: 0,
                            width: 0,
                            height: 0,
                            position: 'absolute',
                            transform: 'translateX(-100vw)'
                          }}
                          type="file"
                          accept="image/png"
                          onChange={e => {
                            const file = e.target.files[0];
                            if (file) {
                              if (file.type !== 'image/png') {
                                message.error('Only PNG files are allowed.');
                                return;
                              }
                              const objectUrl = URL.createObjectURL(file);
                              setFieldValue(field.name, { file, url: objectUrl });
                            }
                          }}
                        />
                        {field.value && field.value.url && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              marginTop: '10px'
                            }}
                          >
                            <img
                              src={field.value.url}
                              style={{
                                maxWidth: 'min(120px, 15vw)',
                                maxHeight: 'min(80px, 10vh)',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                padding: '5px'
                              }}
                              alt="voucher preview"
                            />
                          </div>
                        )}
                      </FormItem>
                    </>
                  );
                }}
              </Field>
              <Field name={'cost'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Cost'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            precision={0}
                            allowClear
                            placeholder={'Cost'}
                            {...field}
                          />
                        </Col>
                        <Col>
                          <Label>Credit Card Point = 1 voucher point</Label>
                        </Col>
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>

              <Field name={'category'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Category'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input allowClear placeholder={'Enter Category'} {...field} />
                        </Col>
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'lrs_reward_code'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'LRS Reward Code'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input
                        allowClear
                        placeholder={'Enter LRS Reward Code (not visible to customers)'}
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'card_ids'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Applicable Credit Cards'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Select
                        {...field}
                        allowClear
                        style={{ minWidth: 300, maxWidth: 800 }}
                        size="large"
                        mode="multiple"
                        options={cardOptions}
                        onChange={value => {
                          setFieldValue(field.name, value);
                        }}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <FormItem {...formItemLayout} label="Start date" required>
                <Row gutter={16} align="middle">
                  <Field name="start_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('start_date', value)}
                            datePickerId="start_date_date_picker"
                            timePickerId="start_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <FormItem {...formItemLayout} label="End date" required>
                <Row gutter={16} align="middle">
                  <Field name="end_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('end_date', value)}
                            datePickerId="end_date_date_picker"
                            timePickerId="end_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <ModalFormFooter>
                <Button
                  type="default"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button htmlType="submit" loading={isSubmitting} type="primary">
                  Save
                </Button>
              </ModalFormFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddEditVouchersModal;
